import React, { useCallback, useEffect, useState } from 'react';
import css from './ExpandableDropdown.module.css'; // Include the styles
import { languages } from '../../config/configListing';
import classNames from 'classnames';
import { useIntl } from '../../util/reactIntl';
import IconCollection from '../../components/IconCollection/IconCollection';
import { OutsideClickHandler } from '../../components';

const ExpandableDropdown = ({ form, initialValues = [] }) => {
  const intl = useIntl();
  const [expandedCategory, setExpandedCategory] = useState();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLanguages, setSelectedLanguages] = useState(initialValues);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [focusedCategory, setFocusedCategory] = useState(null);

  const toggleDropdown = useCallback(() => setIsOpen(prev => !prev), [isOpen]);
  const toggleCategory = label => {
    setExpandedCategory(expandedCategory === label ? null : label);
  };

  const isParentSelected = item => {
    return selectedLanguages.some(({ parent }) => parent === item);
  };

  const isChildSelected = (parent, child) => {
    return selectedLanguages.some(
      selected => selected.parent === parent && selected.child === child
    );
  };

  const handleArrowKeys = e => {
    if (!isOpen) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (focusedCategory === null) {
        setFocusedIndex((prev) => (prev + 1) % languages.length);
      } else {
        const levels = languages.find(lang => lang.label === focusedCategory).levels;
        setFocusedIndex((prev) => (prev + 1) % levels.length);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (focusedCategory === null) {
        setFocusedIndex((prev) => (prev - 1 + languages.length) % languages.length);
      } else {
        const levels = languages.find(lang => lang.label === focusedCategory).levels;
        setFocusedIndex((prev) => (prev - 1 + levels.length) % levels.length);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (focusedCategory === null) {
        setFocusedCategory(languages[focusedIndex].label);
        toggleCategory(languages[focusedIndex].label);
      } else if (!expandedCategory || focusedCategory !== expandedCategory) {
        toggleCategory(languages[focusedIndex].label);
      } else {
        const selectedLevel = languages.find(lang => lang.label === focusedCategory).levels[focusedIndex];
        if (selectedLevel) {
          setSelectedLanguages(prev => {
            if (isParentSelected(focusedCategory)) return prev;
            const newSelection = { parent: focusedCategory, child: selectedLevel.key };
            form.change('languages', [...prev, newSelection]);
            return [...prev, newSelection];
          });
        }
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setFocusedCategory(languages[0].label);
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isOpen) {
      window.addEventListener('keydown', handleArrowKeys);
    } else {
      window.removeEventListener('keydown', handleArrowKeys);
    }
    return () =>
      typeof window !== 'undefined' && window.removeEventListener('keydown', handleArrowKeys);
  }, [isOpen, focusedCategory, focusedIndex]);

  return (
    <div className={css.dropdownContainerWrapper}>
      <OutsideClickHandler onOutsideClick={() => setIsOpen(false)}>
        <label>Languages spoken</label>
        <div className={css.dropdownContainer}>
          {/* Dropdown header */}
          <div className={css.dropdownHeader} onClick={toggleDropdown}>
            {intl.formatMessage({ id: 'EditListingDetailsForm.languagePlaceholder' })}
            <span className={css.dropdownArrow}>
              {isOpen ? (
                <IconCollection className={css.openedSvg} icon="icon-arrow-up-black" />
              ) : (
                <IconCollection icon="icon-arrow-down-black" />
              )}
            </span>
          </div>

          {/* Dropdown menu */}
          {isOpen && (
            <div className={css.dropdownMenu}>
              {languages
                .sort((a, b) => a.label.localeCompare(b.label))
                .map(({ label, levels }, index) => (
                  <div key={label}>
                    <div
                      className={classNames(css.dropdownCategory, {
                        [css.focused]: focusedCategory === null && focusedIndex === index,
                        [css.selectedParent]: isParentSelected(label),
                      })}
                      onClick={() => toggleCategory(label)}
                    >
                      {label}
                      {levels.length > 0 && (
                        <span className={css.dropdownArrow}>
                          {expandedCategory === label ? (
                            <IconCollection className={css.openedSvg} icon="icon-arrow-up-black" />
                          ) : (
                            <IconCollection icon="icon-arrow-down-black" />
                          )}
                        </span>
                      )}
                    </div>
                    {expandedCategory === label && levels.length > 0 && (
                      <div className={css.dropdownLevels}>
                        {levels.map((level, levelIndex) => (
                          <div
                            key={level.key}
                            className={classNames(css.dropdownLevel, {
                              [css.focused]:
                                focusedCategory === label && focusedIndex === levelIndex,
                              [css.selected]: isChildSelected(label, level.key),
                            })}
                            onClick={() => {
                              setSelectedLanguages(prev => {
                                if (selectedLanguages.length < 10) {
                                  const existingIndex = prev.findIndex(
                                    item => item.parent === label
                                  );
                                  if (existingIndex !== -1) {
                                    const updatedLanguages = [...prev];
                                    // remove the entry , if already selected
                                    if (updatedLanguages[existingIndex].child === level.key) {
                                      updatedLanguages.splice(existingIndex, 1);
                                    } else {
                                      updatedLanguages[existingIndex] = {
                                        parent: label,
                                        child: level.key,
                                      };
                                    }
                                    form.change('languages', updatedLanguages);
                                    return updatedLanguages;
                                  }
                                  // add a new selection, if not already selected
                                  const payload = [...prev, { parent: label, child: level.key }];
                                  form.change('languages', payload);
                                  return payload;
                                }
                                return prev;
                              });
                            }}
                          >
                            {level.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
            </div>
          )}
        </div>
        <div className={css.selectedLanguages}>
          {selectedLanguages.map(({ parent, child }) => (
            <div key={`${parent} - ${child}`}>
              {`${parent} - ${child}`}
              <span
                onClick={() =>
                  setSelectedLanguages(prev => {
                    let updated = [...prev];
                    const index = updated.findIndex(l => l.parent === parent && l.child === child);

                    if (index !== -1) {
                      updated.splice(index, 1);
                    }

                    form.change('languages', updated);
                    return updated;
                  })
                }
              >
                &nbsp;
                <IconCollection icon="icon-cross-black" />
              </span>
            </div>
          ))}
        </div>
      </OutsideClickHandler>
    </div>
  );
};

export default ExpandableDropdown;
